import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Award, Film, PlayCircle, Eye, EyeOff, Lock } from 'lucide-react';
import { SectionTitle } from '../SectionTitle';
import { fetchMyOfficialHighlights, friendlyError, buildAssetUrl } from '@/lib/api';
import { toast } from 'sonner';
import { VideoDownloadInline } from '@/components/downloads/VideoDownloadInline';

const ACCESS_BADGE = {
  free: { label: 'Gratis', cls: 'bg-emerald-100 text-emerald-800' },
  premium: { label: 'Premium', cls: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]' },
  purchase: { label: 'Compra individual', cls: 'bg-sky-100 text-sky-800' },
};

export function MyOfficialHighlightsSection() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0 });

  useEffect(() => {
    (async () => {
      try { setData(await fetchMyOfficialHighlights()); }
      catch (err) { toast.error(friendlyError(err)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <Card id="mis-highlights-oficiales" className="p-6 rounded-2xl bg-white border border-[var(--ns-border)]">
        <Skeleton className="h-6 w-64 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </Card>
    );
  }

  const items = data?.items || [];
  return (
    <Card
      id="mis-highlights-oficiales"
      data-testid="my-official-highlights-section"
      className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)] scroll-mt-24"
    >
      <SectionTitle icon={Award} label="Mis highlights oficiales" hint="Clips oficiales asociados a tu perfil." />
      <p className="text-sm text-[var(--ns-text-muted)] -mt-2 mb-4">
        Aquí puedes consultar los highlights oficiales que tu club o NetScouters ha asociado a tu perfil.
      </p>
      {items.length === 0 ? (
        <div data-testid="my-highlights-empty" className="p-6 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-bg-2)]/40 text-center">
          <Film className="h-10 w-10 text-[var(--ns-text-subtle)] mx-auto mb-2" />
          <p className="text-sm text-[var(--ns-text-muted)]">
            Todavía no tienes highlights oficiales asignados.
          </p>
        </div>
      ) : (
        <ul data-testid="my-highlights-list" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((h) => {
            const acc = ACCESS_BADGE[h.video_access_type] || null;
            const eventDate = h.event_date ? new Date(h.event_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
            return (
              <li key={h.assignment_id} data-testid={`my-highlight-${h.assignment_id}`} className="flex flex-col rounded-xl bg-white border border-[var(--ns-border)] overflow-hidden">
                <div className="relative aspect-video bg-[var(--ns-bg-2)]">
                  {h.video_thumbnail ? (
                    <img src={buildAssetUrl(h.video_thumbnail)} alt={h.video_title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-[var(--ns-text-subtle)]"><Film className="h-8 w-8" /></div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {acc ? <Badge className={`text-[10px] uppercase ${acc.cls}`}>{acc.label}</Badge> : null}
                  </div>
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1.5">
                  <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{h.video_title}</p>
                  <p className="text-xs text-[var(--ns-text-muted)] truncate">
                    {h.event_name}{eventDate ? ` · ${eventDate}` : ''}
                  </p>
                  <p className="text-[11px] text-[var(--ns-text-subtle)]">Acción: <span className="font-semibold text-[var(--ns-text)]">{h.action_type || 'Otro'}</span></p>
                  <div className="flex items-center gap-2 mt-1">
                    {h.is_visible_in_public_portfolio ? (
                      <span className="text-[10px] inline-flex items-center gap-1 text-[var(--ns-gold-700)] font-semibold">
                        <Eye className="h-3 w-3" /> Visible en mi perfil público
                      </span>
                    ) : (
                      <span className="text-[10px] inline-flex items-center gap-1 text-[var(--ns-text-subtle)]">
                        <EyeOff className="h-3 w-3" /> No visible públicamente
                      </span>
                    )}
                  </div>
                  {h.event_slug ? (
                    <Button asChild size="sm" data-testid={`my-highlight-play-${h.assignment_id}`} className="mt-2 h-8 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-xs font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0">
                      <Link to={`/eventos/${h.event_slug}`} className="inline-flex items-center gap-1">
                        <PlayCircle className="h-3.5 w-3.5" /> Abrir en el partido
                      </Link>
                    </Button>
                  ) : (
                    <span className="mt-2 text-[10px] inline-flex items-center gap-1 text-[var(--ns-text-subtle)]"><Lock className="h-3 w-3" /> Enlace no disponible</span>
                  )}
                  {/* Fase 06D · descarga del contenido oficial (mismo vídeo) */}
                  <VideoDownloadInline videoId={h.video_id} compact />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[11px] text-[var(--ns-text-subtle)] mt-3">
        Solo lectura. Tus highlights los asigna y gestiona tu club. Las reglas comerciales del contenido (registro, Premium, compra) se mantienen al reproducir.
      </p>
    </Card>
  );
}

// Alias mantenido por compatibilidad con importaciones antiguas.
export const MyOfficialHighlightsTab = MyOfficialHighlightsSection;
